/**
 * Build enrolled program deliverable rows for an account (same rules as ClientOverview loadProgramData).
 */
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { computeCustomProgramMonthlyBaseFromAccount, isStrategyCallDeliverableName } from './programPricing';
import { applyDueScheduledDeliverableChanges } from '../services/scheduledDeliverableChangesService';

export function itemDeliverableAppliesToProgram(mergedItem, programId) {
  if (!programId || programId === 'Custom') return true;
  const programs = mergedItem?.deliverable?.programs;
  if (!programs || !Array.isArray(programs) || programs.length === 0) return true;
  return programs.includes(programId);
}

export function rowHasDeliverableTracking(contractItem, matchingAlaCarte) {
  return !!(contractItem?.hasDeliverable || matchingAlaCarte?.hasDeliverable);
}

export function isOneTimeStylePeriod(periodRaw) {
  const compact = String(periodRaw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  return !compact || compact === 'onetime';
}

export function resolveDeliverableProgress(account, rowId, catalogId) {
  const a = account?.deliverables?.[rowId];
  const b = catalogId && String(catalogId) !== String(rowId) ? account?.deliverables?.[catalogId] : null;
  if (!a && !b) return { logs: [] };
  if (!b) return { logs: [], ...a };
  if (!a) return { logs: [], ...b };
  const logMap = new Map();
  [...(a.logs || []), ...(b.logs || [])].forEach((l) => {
    const k = l?.id != null ? String(l.id) : `orphan-${l?.timestamp || Math.random()}`;
    if (!logMap.has(k)) logMap.set(k, l);
  });
  const logs = Array.from(logMap.values());
  return {
    ...b,
    ...a,
    logs,
    lastCompleted: a.lastCompleted ?? b.lastCompleted ?? null,
    purchasedAddOns: { ...(b.purchasedAddOns || {}), ...(a.purchasedAddOns || {}) },
  };
}

export function isMonthlyDeliverablePeriod(periodRaw) {
  const p = String(periodRaw || '').trim();
  return p === 'Month' || p === 'Monthly';
}

export function formatLocalDateKey(date) {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatAccountLogDisplayDate(value) {
  const d = parseFlexibleAccountDate(value);
  return d ? d.toLocaleDateString() : '—';
}

function getBillingPeriodLengthMs(acc) {
  if (acc?.billing?.currentPeriodStart != null && acc?.billing?.currentPeriodEnd != null) {
    const s = parseFlexibleAccountDate(acc.billing.currentPeriodStart);
    const e = parseFlexibleAccountDate(acc.billing.currentPeriodEnd);
    if (s && e && e > s) return e.getTime() - s.getTime();
  }
  return 30 * 24 * 60 * 60 * 1000;
}

/**
 * Add a fixed number of days to fromDate. Built on the local-date constructor rather than
 * millisecond arithmetic so a DST boundary inside the span cannot shift the result by an hour.
 */
function addDaysFromDate(fromDate, days) {
  return new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate() + days);
}

/**
 * Selectable lengths for the program minimum term. `days` is a fixed day count, not calendar
 * months: a 6-month minimum term ends 180 days after the start date, not on the same day-of-month
 * six months later.
 */
export const MINIMUM_TERM_OPTIONS = [
  { value: '1', label: '1 Month', days: 30 },
  { value: '6', label: '6 Months', days: 180 },
  { value: '12', label: '1 Year', days: 365 },
];

export function getMinimumTermOption(value) {
  if (value == null || value === '') return null;
  return MINIMUM_TERM_OPTIONS.find((option) => option.value === String(value)) || null;
}

/**
 * Add N calendar months to fromDate, snapping the day to anchorDay (clamped to end of month).
 * Supports negative months for backward stepping.
 */
function addCalendarMonthsFromDate(fromDate, months, anchorDay) {
  const rawMonth = fromDate.getMonth() + months;
  const targetYear = fromDate.getFullYear() + Math.floor(rawMonth / 12);
  const normalizedMonth = ((rawMonth % 12) + 12) % 12;
  const maxDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  return new Date(targetYear, normalizedMonth, Math.min(anchorDay, maxDay));
}

/**
 * Resolve the month-key portion of a period key (YYYY-MM-DD → YYYY-MM; YYYY-MM → YYYY-MM).
 * Used to match add-on keys written by checkout (YYYY-MM) against billing period keys (YYYY-MM-DD).
 */
export function getMonthKeyFromPeriodKey(periodKey) {
  if (!periodKey) return '';
  return String(periodKey).slice(0, 7);
}

/**
 * Look up purchased add-ons for a period. Checks both the exact periodKey (YYYY-MM-DD Stripe format)
 * and the month-only key (YYYY-MM used by checkout webhook), then sums them (they are mutually exclusive).
 */
export function resolveAddOnPurchased(progress, periodKey) {
  if (!progress?.purchasedAddOns || !periodKey) return 0;
  const exactAmt = progress.purchasedAddOns[periodKey] || 0;
  const monthKey = getMonthKeyFromPeriodKey(periodKey);
  const monthAmt = (monthKey && monthKey !== periodKey) ? (progress.purchasedAddOns[monthKey] || 0) : 0;
  return exactAmt + monthAmt;
}

/**
 * This-period-only quantity bumps (account.periodDeliverables) for one deliverable. The CRM stamps
 * each entry with getCurrentDeliverablePeriodKey, which is the same strategy-call period key, so
 * only entries for the current period count. Matches the row id or its catalog id.
 */
export function resolvePeriodOnlyQuantity(account, periodKey, rowId, catalogId) {
  const list = Array.isArray(account?.periodDeliverables) ? account.periodDeliverables : [];
  if (!periodKey || list.length === 0) return 0;
  const ids = new Set([rowId, catalogId].filter(Boolean).map(String));
  return list.reduce((sum, entry) => {
    if (!entry || entry.periodKey !== periodKey || !ids.has(String(entry.id))) return sum;
    const qty = typeof entry.quantity === 'number' ? entry.quantity : parseInt(entry.quantity, 10) || 0;
    return qty > 0 ? sum + qty : sum;
  }, 0);
}

/**
 * Deliverables always reset on a 30-day cadence measured from the client's start date, no matter
 * how often they are BILLED. A weekly, quarterly, or annual payer gets the exact same monthly
 * deliverable allotment as a monthly payer — the payment frequency only changes when money moves,
 * not when deliverables refresh. So the Stripe billing period (which reflects payment cadence: 7
 * days weekly, ~365 annually) is only allowed to drive the reset date when the client is billed
 * monthly; every other cycle rolls forward in fixed 30-day terms from the start date.
 */
function isMonthlyBillingCycle(billingCycle) {
  return billingCycle === 'monthly' || billingCycle === 'month' || billingCycle === '';
}

function buildBillingPeriod(periodStart, periodEnd) {
  const displayStart = periodStart.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const inclusiveEnd = new Date(periodEnd.getTime() - 1);
  const displayEnd = inclusiveEnd.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return {
    periodKey: formatLocalDateKey(periodStart),
    periodStart,
    periodEnd,
    displayLabel: `${displayStart} – ${displayEnd}`,
  };
}

export function filterLogsForCurrentPeriod(logs, itemPeriodRaw, currentPeriod, billingPeriod) {
  if (billingPeriod) {
    return logs.filter((log) => {
      // scheduledAt = when the booking was MADE (call logs). Usage counts against the period the
      // call was scheduled in, not the period the meeting lands in — log.date stays the meeting
      // date for display. Logs without scheduledAt (manual CRM adds, other deliverables) keep
      // date-based attribution.
      const logDate =
        parseFlexibleAccountDate(log.scheduledAt) ||
        parseFlexibleAccountDate(log.date) ||
        parseFlexibleAccountDate(log.timestamp);
      if (!logDate) return false;
      return logDate >= billingPeriod.periodStart && logDate < billingPeriod.periodEnd;
    });
  }
  if (isOneTimeStylePeriod(itemPeriodRaw)) {
    return logs.filter(
      (log) =>
        !log.period ||
        log.period === currentPeriod ||
        isOneTimeStylePeriod(log.period)
    );
  }
  return logs.filter((log) => log.period === currentPeriod);
}

export function parseFlexibleAccountDate(value) {
  if (value == null || value === '') return null;
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (ymd) {
    const y = Number(ymd[1]);
    const mo = Number(ymd[2]);
    const day = Number(ymd[3]);
    const d = new Date(y, mo - 1, day || 1);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function getClientActualStartDate(account, contractDataFromState) {
  if (!account) return null;
  const programStart = parseFlexibleAccountDate(account.program?.startDate);
  if (programStart) return programStart;

  const contractStart = parseFlexibleAccountDate(account.contractStart);
  if (contractStart) return contractStart;

  const contract = contractDataFromState || account.signedContract || account.contractData;
  const fromContract = parseFlexibleAccountDate(contract?.startDate);
  if (fromContract) return fromContract;

  const subStatus = String(account.billing?.status || '').toLowerCase();
  if (subStatus === 'trialing' || subStatus === 'trial') {
    return null;
  }

  return parseFlexibleAccountDate(account.billing?.currentPeriodStart);
}

export function getCurrentPeriod(period) {
  const p = (period || '').trim();
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const quarter = Math.ceil(month / 3);
  const week = Math.ceil(now.getDate() / 7);

  switch (p) {
    case 'One Time':
      return 'One Time';
    case 'Week':
      return `${year}-W${week}`;
    case 'Month':
    case 'Monthly':
      return `${year}-${month.toString().padStart(2, '0')}`;
    case 'Quarter':
      return `${year}-Q${quarter}`;
    case 'Year':
      return year.toString();
    default:
      return 'One Time';
  }
}

export function getAccountStrategyCallPeriod(acc, contractDataFromState) {
  if (!acc) return null;
  const now = new Date();

  const billingCycle = String(
    acc.billing?.billingCycle || acc.billingCycle || acc.program?.billingCycle || 'monthly'
  ).toLowerCase();

  // Stripe's billing period is authoritative ONLY while it's live (end date still in the future) AND
  // the client is billed monthly — a monthly Stripe period matches the 30-day deliverable cadence,
  // so honoring it keeps the reset on the true billing anniversary. Weekly / quarterly / annual
  // payers skip it (their period is 7 / ~90 / ~365 days, none of which is the deliverable cadence)
  // and fall through to the 30-day roll-forward. A stale monthly period (end date already passed)
  // also falls through, so the displayed reset date is never in the past.
  if (
    isMonthlyBillingCycle(billingCycle) &&
    acc.billing?.currentPeriodStart != null &&
    acc.billing?.currentPeriodEnd != null
  ) {
    const periodStart = parseFlexibleAccountDate(acc.billing.currentPeriodStart);
    const periodEnd = parseFlexibleAccountDate(acc.billing.currentPeriodEnd);
    if (periodStart && periodEnd && periodEnd.getTime() > now.getTime()) {
      return buildBillingPeriod(periodStart, periodEnd);
    }
  }

  const anchor = getClientActualStartDate(acc, contractDataFromState);
  if (!anchor) return null;

  const anchorStart = new Date(anchor);
  anchorStart.setHours(0, 0, 0, 0);

  // Deliverables reset every 30 days from the start date for every billing cycle (see
  // isMonthlyBillingCycle above), rolling forward by however many whole terms have elapsed.
  const periodLengthMs = 30 * 24 * 60 * 60 * 1000;
  let periodEnd = new Date(anchorStart.getTime() + periodLengthMs);
  while (periodEnd.getTime() <= now.getTime()) {
    periodEnd = new Date(periodEnd.getTime() + periodLengthMs);
  }
  const periodStart = new Date(periodEnd.getTime() - periodLengthMs);
  return buildBillingPeriod(periodStart, periodEnd);
}

export function resolveBillingPeriodContainingDate(acc, contractDataFromState, targetDateRaw) {
  const target = parseFlexibleAccountDate(targetDateRaw);
  if (!target || !acc) return null;

  const billingCycle = String(
    acc.billing?.billingCycle || acc.billingCycle || acc.program?.billingCycle || 'monthly'
  ).toLowerCase();

  // Check Stripe current period first (most authoritative) — but only for monthly payers, whose
  // Stripe period matches the 30-day deliverable cadence. Weekly / quarterly / annual periods do
  // not, so those fall through to the 30-day bucketing. Mirrors getAccountStrategyCallPeriod.
  if (
    isMonthlyBillingCycle(billingCycle) &&
    acc.billing?.currentPeriodStart != null &&
    acc.billing?.currentPeriodEnd != null
  ) {
    const periodStart = parseFlexibleAccountDate(acc.billing.currentPeriodStart);
    const periodEnd = parseFlexibleAccountDate(acc.billing.currentPeriodEnd);
    if (periodStart && periodEnd && target >= periodStart && target < periodEnd) {
      return buildBillingPeriod(periodStart, periodEnd);
    }
  }

  let anchor = getClientActualStartDate(acc, contractDataFromState);
  if (!anchor) {
    anchor = parseFlexibleAccountDate(acc.billing?.currentPeriodStart);
  }

  if (!anchor) {
    // No anchor: fall back to calendar month boundaries
    const y = target.getFullYear();
    const m = target.getMonth();
    return buildBillingPeriod(new Date(y, m, 1), new Date(y, m + 1, 1));
  }

  const anchorStart = new Date(anchor);
  anchorStart.setHours(0, 0, 0, 0);

  // Deliverables reset every 30 days from the start date for every billing cycle — walk forward or
  // backward in 30-day terms to find the one containing the target date.
  const periodLengthMs = 30 * 24 * 60 * 60 * 1000;
  let pStart = new Date(anchorStart);
  let pEnd = new Date(anchorStart.getTime() + periodLengthMs);
  while (target >= pEnd) {
    pStart = new Date(pEnd);
    pEnd = new Date(pStart.getTime() + periodLengthMs);
  }
  while (target < pStart) {
    pEnd = new Date(pStart);
    pStart = new Date(pEnd.getTime() - periodLengthMs);
  }
  return buildBillingPeriod(pStart, pEnd);
}

export function groupDeliverableLogsByBillingPeriod(logs, acc, contractDataFromState) {
  const groups = new Map();
  (logs || []).forEach((log) => {
    const period = resolveBillingPeriodContainingDate(
      acc,
      contractDataFromState,
      log.scheduledAt || log.date || log.timestamp
    );
    const key = period?.periodKey || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        periodKey: key,
        displayLabel: period?.displayLabel || key,
        logs: [],
        totalCompleted: 0,
      });
    }
    const group = groups.get(key);
    group.logs.push(log);
    group.totalCompleted += log.quantity || 0;
  });
  return Array.from(groups.values()).sort((a, b) => b.periodKey.localeCompare(a.periodKey));
}

/**
 * End date of the program minimum term, in authority order:
 *
 *   1. program.minimumTerm — set deliberately on the CRM Program tab. Wins over the contract
 *      everywhere this date surfaces (Program tab, client portal, finance reports) so the CRM and
 *      the client cannot disagree about the same date. Measured in fixed days from the start.
 *   2. contract startDate + contractLength — the signed commitment, in calendar months.
 *   3. program.termEndDate — legacy saved value, never written by current code. Ranked last so a
 *      stale value cannot shadow a real commitment.
 */
export function getTermEndDateFromAccount(account, contractDataFromState) {
  const minimumTerm = getMinimumTermOption(account?.program?.minimumTerm);
  if (minimumTerm) {
    const startDate = getClientActualStartDate(account, contractDataFromState);
    if (startDate) return addDaysFromDate(startDate, minimumTerm.days);
  }

  const contract = contractDataFromState || account?.signedContract || account?.contractData;
  const startDateStr = contract?.startDate || account?.program?.startDate;
  const contractLengthStr = contract?.contractLength;

  if (startDateStr && contractLengthStr) {
    const baseDate = parseFlexibleAccountDate(startDateStr);
    const monthsToAdd = parseInt(contractLengthStr, 10);
    if (baseDate && monthsToAdd && !Number.isNaN(monthsToAdd)) {
      return addCalendarMonthsFromDate(baseDate, monthsToAdd, baseDate.getDate());
    }
  }

  // Legacy fallback: explicit saved override.
  if (account?.program?.termEndDate) {
    const saved = new Date(account.program.termEndDate);
    if (!isNaN(saved.getTime())) return saved;
  }

  return null;
}

export function getRenewsOnDateFromAccount(account, contractDataFromState) {
  const billingPeriod = getAccountStrategyCallPeriod(account, contractDataFromState);
  if (billingPeriod?.periodEnd) return billingPeriod.periodEnd;
  return null;
}

/**
 * Parse a legacy locale-formatted cancel date string like "1/15/2026" or "12/31/2025".
 * Returns null if not parseable.
 */
function parseLegacyCancelDateString(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Resolve the canonical lifecycle state for an account.
 *
 * Authority order (hybrid):
 *   1. program.lifecycleOverrides.* — explicit CRM override (always wins)
 *   2. billing.* from Stripe — authoritative when stripeSubscriptionId is present
 *   3. program.cancelState / program.subscriptionStatus — manual fallback
 *
 * Returns:
 *   { status, startDate, termEndDate, periodStart, periodEnd, renewsOn,
 *     cancelType ('renews'|'cancels'), cancelEffectiveDate }
 */
export function resolveAccountLifecycle(account, contractDataFromState) {
  if (!account) {
    return {
      status: 'active', startDate: null, termEndDate: null,
      periodStart: null, periodEnd: null, renewsOn: null,
      cancelType: 'renews', cancelEffectiveDate: null,
    };
  }

  const billing = account.billing || {};
  const program = typeof account.program === 'object' && account.program !== null ? account.program : {};
  const overrides = program.lifecycleOverrides || {};
  const hasStripe = !!(billing.stripeSubscriptionId);

  // 1. Status: override → Stripe → manual
  let status;
  if (overrides.status) {
    status = overrides.status;
  } else if (hasStripe && billing.status) {
    status = billing.status;
  } else {
    status = program.subscriptionStatus || program.clientStatus || billing.status || 'active';
  }

  // 2. Start Date
  const startDate = getClientActualStartDate(account, contractDataFromState);

  // 3. Term End
  const termEndDate = getTermEndDateFromAccount(account, contractDataFromState);

  // 4. Billing period
  const billingPeriod = getAccountStrategyCallPeriod(account, contractDataFromState);
  const periodStart = billingPeriod?.periodStart || null;
  const periodEnd = billingPeriod?.periodEnd || null;
  const renewsOn = periodEnd;

  // 5. Cancel state: explicit override → Stripe → manual program.cancelState (legacy string)
  let cancelType = 'renews';
  let cancelEffectiveDate = null;

  if (overrides.cancelState === 'renews') {
    cancelType = 'renews';
  } else if (overrides.cancelState === 'cancels') {
    cancelType = 'cancels';
    cancelEffectiveDate = overrides.cancelEffectiveDate
      ? parseFlexibleAccountDate(overrides.cancelEffectiveDate)
      : null;
  } else if (hasStripe && billing.cancelAtPeriodEnd) {
    cancelType = 'cancels';
    if (billing.cancelAt) {
      cancelEffectiveDate = parseFlexibleAccountDate(billing.cancelAt);
    } else {
      cancelEffectiveDate = periodEnd; // defaults to current period end
    }
  } else {
    // Manual fallback: parse legacy program.cancelState string
    const cs = String(program.cancelState || 'Renews');
    if (cs.toLowerCase() === 'renews' || !cs) {
      cancelType = 'renews';
    } else if (cs.startsWith('Cancels on ')) {
      cancelType = 'cancels';
      const rawDate = cs.slice('Cancels on '.length).trim();
      cancelEffectiveDate =
        parseFlexibleAccountDate(rawDate) ||
        parseLegacyCancelDateString(rawDate);
    }
  }

  return {
    status,
    startDate,
    termEndDate,
    periodStart,
    periodEnd,
    renewsOn,
    cancelType,
    cancelEffectiveDate,
  };
}

export function findStrategyCallDeliverableContext(account, contractItems, itemsData, programStatus) {
  let strategyCallItem = (contractItems || []).find((item) => isStrategyCallDeliverableName(item.name));

  if (!strategyCallItem && programStatus === 'Custom' && Array.isArray(account?.customProgramDeliverables)) {
    const customIds = account.customProgramDeliverables;
    const customQuantities = account.customProgramDeliverableQuantities || {};
    const customStrategyId = customIds.find((id) => {
      const ala = itemsData.find((i) => i.id === id);
      return isStrategyCallDeliverableName(ala?.name);
    });
    if (customStrategyId) {
      const ala = itemsData.find((i) => i.id === customStrategyId);
      const qty = customQuantities[customStrategyId] ?? 1;
      strategyCallItem = {
        id: customStrategyId,
        name: ala?.name || '1:1 Strategy Call',
        quantity: String(qty),
        deliverable: ala?.deliverable || {},
      };
    }
  }

  if (!strategyCallItem) return null;

  const matchingAlaCarte = itemsData.find(
    (i) =>
      i.id === strategyCallItem.id ||
      (i.name || '').toLowerCase() === (strategyCallItem.name || '').toLowerCase()
  );

  let programQty =
    strategyCallItem.quantity === '0' ? 1 : parseInt(strategyCallItem.quantity, 10) || 1;

  if (programStatus === 'Custom' && Array.isArray(account?.customProgramDeliverables)) {
    const customQuantities = account.customProgramDeliverableQuantities || {};
    const customQty =
      customQuantities[strategyCallItem.id] ??
      (matchingAlaCarte ? customQuantities[matchingAlaCarte.id] : undefined);
    if (typeof customQty === 'number' && !Number.isNaN(customQty)) {
      programQty = customQty;
    }
  }

  // deliverableQuantityOverrides is the top-precedence source for EVERY program status, matching how
  // the deliverables list applies it (fetchProgramDeliverablesBundle). This is what makes a CRM
  // quantity edit stick for a Custom account that was switched over via the status dropdown (which
  // never seeds customProgramDeliverables, so handleAdjustDeliverableQuantity writes the override).
  const overrides = account?.deliverableQuantityOverrides || {};
  const overrideQty =
    overrides[strategyCallItem.id] ??
    (matchingAlaCarte ? overrides[matchingAlaCarte.id] : undefined);
  if (typeof overrideQty === 'number' && !Number.isNaN(overrideQty)) {
    programQty = overrideQty;
  }

  return {
    rowId: strategyCallItem.id,
    catalogId: matchingAlaCarte?.id,
    programQty,
  };
}

export function computeStrategyCallUsage(account, contractDataFromState, context) {
  if (!account || !context) return null;

  const billingPeriod = getAccountStrategyCallPeriod(account, contractDataFromState);
  if (!billingPeriod) return null;

  const progress = resolveDeliverableProgress(account, context.rowId, context.catalogId);
  const logs = progress.logs || [];
  const currentPeriodLogs = filterLogsForCurrentPeriod(
    logs,
    'Month',
    billingPeriod.periodKey,
    billingPeriod
  );
  const used = currentPeriodLogs.reduce((sum, log) => sum + (log.quantity || 0), 0);
  const purchasedThisPeriod = resolveAddOnPurchased(progress, billingPeriod.periodKey);
  // CRM "add for this period only" bumps — the Program tab row already counts them, so the pool must too.
  const periodOnlyQty = resolvePeriodOnlyQuantity(
    account,
    billingPeriod.periodKey,
    context.rowId,
    context.catalogId
  );

  return {
    used,
    limit: context.programQty + purchasedThisPeriod + periodOnlyQty,
    periodDisplay: billingPeriod.displayLabel,
    periodEnd: billingPeriod.periodEnd,
    billingPeriod,
  };
}

function resolveMonthlyDeliverablePeriodContext(account, contractDataFromState, period) {
  const billingPeriod = isMonthlyDeliverablePeriod(period)
    ? getAccountStrategyCallPeriod(account, contractDataFromState)
    : null;
  const currentPeriod = billingPeriod ? billingPeriod.periodKey : getCurrentPeriod(period);
  return { billingPeriod, currentPeriod };
}

/**
 * The key that identifies a client's CURRENT billing period for one-period-only deliverables.
 * Uses the account's 30-day billing period start (YYYY-MM-DD); falls back to the current YYYY-MM
 * month when no start date is resolvable. The CRM stamps this same key when adding a one-period
 * deliverable, so an entry only "matches" while its period is current — the moment the period rolls
 * over, the key stops matching and the deliverable disappears.
 */
export function getCurrentDeliverablePeriodKey(account, contractDataFromState = null) {
  const billingPeriod = getAccountStrategyCallPeriod(account, contractDataFromState);
  if (billingPeriod?.periodKey) return billingPeriod.periodKey;
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const PROGRAM_STATUS_MAP = {
  Lite: 'Lite',
  Plus: 'Plus',
  Pro: 'Pro',
  Hourly: 'Hourly',
  '5 Hours': '5 Hours',
  '10 Hours': '10 Hours',
  'A La Carte': 'A La Carte',
  Custom: 'Custom',
};

/**
 * Loads alaCarteItems + programs and returns deliverable rows + optional programPriceData for CRM / portal.
 */
export async function fetchProgramDeliverablesBundle(account, contractDataFromState = null) {
  // Scheduled next-period changes apply LAZILY: the first bundle build after the reset date folds
  // due entries into the real roster fields and logs them. When that happens, the rest of this
  // build (and the returned appliedAccount) uses the post-apply account so the new period's
  // roster is correct immediately.
  let appliedAccount = null;
  if (account?.id && Array.isArray(account.scheduledDeliverableChanges) && account.scheduledDeliverableChanges.length > 0) {
    try {
      appliedAccount = await applyDueScheduledDeliverableChanges(account);
      if (appliedAccount) account = appliedAccount;
    } catch (err) {
      console.warn('Could not apply scheduled deliverable changes', err);
    }
  }

  const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
  const itemsData = itemsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (!account) {
    return { itemsData, programItems: [], programPriceData: null, appliedAccount };
  }

  const programStatus = typeof account.program === 'string' ? account.program : account?.program?.status;

  if (!programStatus || programStatus === 'Not Started') {
    return { itemsData, programItems: [], programPriceData: null, appliedAccount };
  }

  const programsSnapshot = await getDocs(collection(db, 'programs'));

  let programDoc = null;
  let programData = null;
  let programId = null;

  const mappedId = PROGRAM_STATUS_MAP[programStatus];
  if (mappedId) {
    programDoc = await getDoc(doc(db, 'programs', mappedId));
    if (programDoc.exists()) {
      programData = programDoc.data();
      programId = mappedId;
    }
  }

  if (!programDoc || !programDoc.exists()) {
    programsSnapshot.forEach((d) => {
      const data = d.data();
      if (data.name === programStatus) {
        programData = data;
        programId = d.id;
      }
    });
  }

  if (programStatus === 'Custom') programId = 'Custom';

  const isCustomWithDeliverables =
    programId === 'Custom' &&
    Array.isArray(account.customProgramDeliverables) &&
    account.customProgramDeliverables.length > 0;
  const hasExtraDeliverables =
    Array.isArray(account.extraDeliverables) && account.extraDeliverables.length > 0;

  let programPriceData = null;

  if (!(programData || isCustomWithDeliverables || hasExtraDeliverables)) {
    return { itemsData, programItems: [], programPriceData: null, appliedAccount };
  }

  if (programData) {
    let pricePerMonth = programData.pricePerMonth;
    let monthlyPriceVal = programData.monthlyPrice;
    let priceVal = programData.price;
    if (programId === 'Custom' && isCustomWithDeliverables && itemsData.length > 0) {
      const computed = computeCustomProgramMonthlyBaseFromAccount(
        account,
        itemsData,
        account.billingCycle || account.program?.billingCycle || 'monthly'
      );
      if (computed != null && computed > 0) {
        pricePerMonth = computed;
        monthlyPriceVal = computed;
        priceVal = computed;
      }
    }
    programPriceData = {
      pricePerMonth,
      monthlyPrice: monthlyPriceVal,
      price: priceVal,
      name: programData.name,
      id: programId,
    };
  } else if (isCustomWithDeliverables && itemsData.length > 0) {
    const computed = computeCustomProgramMonthlyBaseFromAccount(
      account,
      itemsData,
      account.billingCycle || account.program?.billingCycle || 'monthly'
    );
    if (computed != null && computed > 0) {
      programPriceData = {
        pricePerMonth: computed,
        monthlyPrice: computed,
        price: computed,
        name: 'Custom',
        id: 'Custom',
      };
    }
  }

  let contractItems = [];
  if (isCustomWithDeliverables) {
    const quantities = account.customProgramDeliverableQuantities || {};
    contractItems = account.customProgramDeliverables
      .map((itemId) => {
        const item = itemsData.find((i) => i.id === itemId);
        if (!item) return null;
        // The custom list carries every item the client picked (it also drives pricing), but
        // only catalog items flagged "has deliverable" are tracked deliverables — the same rule
        // that decides which lines print on the contract. Resources like Sales Scripts or the
        // Courses Library are part of the program without being something the team delivers.
        // An item with logged progress stays visible so history is never orphaned.
        const progress = resolveDeliverableProgress(account, itemId, itemId);
        const hasProgress = (progress.logs && progress.logs.length > 0) || !!progress.lastCompleted;
        if (!item.hasDeliverable && !hasProgress) return null;
        const qty = quantities[itemId] ?? 1;
        return {
          id: item.id,
          name: item.name || item.itemName || item.title || 'Item',
          quantity: String(qty),
          deliverable: item.deliverable,
          ...item,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  } else if (programData) {
    const contractItemsSnapshot = await getDocs(collection(db, 'programs', programId, 'contractItems'));
    contractItems = contractItemsSnapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  let itemsWithDeliverables = [];

  if (contractItems.length > 0) {
    itemsWithDeliverables = contractItems.map((contractItem) => {
      const matchingAlaCarte = itemsData.find(
        (item) => item.id === contractItem.id || item.name === contractItem.name
      );
      const alaCarteDeliverable = matchingAlaCarte?.deliverable || {};
      const contractDeliverable = contractItem?.deliverable || {};
      const programQty = contractItem.quantity === '0' ? 1 : parseInt(contractItem.quantity, 10) || 1;
      const mergedDeliverable = {
        ...contractDeliverable,
        ...alaCarteDeliverable,
        quantity: programQty,
        period:
          alaCarteDeliverable.period ||
          alaCarteDeliverable.frequency ||
          contractDeliverable.period ||
          contractDeliverable.frequency ||
          'One Time',
        frequency:
          alaCarteDeliverable.frequency ||
          alaCarteDeliverable.period ||
          contractDeliverable.frequency ||
          contractDeliverable.period,
      };
      const mergedItem = matchingAlaCarte
        ? { ...matchingAlaCarte, ...contractItem, deliverable: mergedDeliverable }
        : { ...contractItem, deliverable: contractItem?.deliverable || mergedDeliverable };
      const programQuantity = programQty;
      const catalogId = matchingAlaCarte?.id;
      const contractRowId = mergedItem.id;
      const hasDeliverableFlag = rowHasDeliverableTracking(contractItem, matchingAlaCarte);
      const deliverableProgress = resolveDeliverableProgress(account, contractRowId, catalogId);
      const hasStoredProgress =
        (deliverableProgress.logs && deliverableProgress.logs.length > 0) ||
        !!deliverableProgress.lastCompleted;
      const hasDeliverableForProgram =
        (hasDeliverableFlag || hasStoredProgress) &&
        (itemDeliverableAppliesToProgram(mergedItem, programId) || hasStoredProgress);
      if (hasDeliverableForProgram) {
        const logs = deliverableProgress.logs || [];
        const period = mergedItem.deliverable?.period || mergedItem.deliverable?.frequency || 'One Time';
        const isStrategyCall = isStrategyCallDeliverableName(mergedItem.name);
        const { billingPeriod, currentPeriod } = resolveMonthlyDeliverablePeriodContext(
          account,
          contractDataFromState,
          period
        );
        const currentPeriodLogs = filterLogsForCurrentPeriod(
          logs,
          period,
          currentPeriod,
          billingPeriod
        );
        const currentCompleted = currentPeriodLogs.reduce((sum, log) => sum + log.quantity, 0);
        const isCheckboxMode = contractItem.quantity === '0' && isOneTimeStylePeriod(period);
        const purchasedAddOn = isStrategyCall
          ? resolveAddOnPurchased(deliverableProgress, billingPeriod ? billingPeriod.periodKey : currentPeriod)
          : 0;
        const effectiveProgramQuantity = programQty + purchasedAddOn;
        return {
          ...mergedItem,
          deliverableCatalogId:
            catalogId && String(catalogId) !== String(contractRowId) ? catalogId : undefined,
          programQuantity: effectiveProgramQuantity,
          completed: currentCompleted,
          lastCompleted: deliverableProgress.lastCompleted || null,
          currentPeriod,
          isCheckboxMode,
        };
      }
      const period = mergedItem.deliverable?.period || mergedItem.deliverable?.frequency || 'One Time';
      const isCheckboxMode = contractItem.quantity === '0' && isOneTimeStylePeriod(period);
      return {
        ...mergedItem,
        programQuantity,
        completed: 0,
        lastCompleted: null,
        currentPeriod: null,
        isCheckboxMode,
      };
    });
  } else if (programData) {
    const features = programData.features || {};
    Object.keys(features).forEach((featureName) => {
      const featureData = features[featureName];
      const matchingItem = itemsData.find((item) => {
        if (item.name !== featureName || !itemDeliverableAppliesToProgram(item, programId)) return false;
        const p = account.deliverables?.[item.id];
        const hasProgress = (p?.logs && p.logs.length > 0) || !!p?.lastCompleted;
        return !!item.hasDeliverable || hasProgress;
      });
      if (matchingItem) {
        const deliverableProgress = account.deliverables?.[matchingItem.id] || {};
        const logs = deliverableProgress.logs || [];
        const period = matchingItem.deliverable?.period || matchingItem.deliverable?.frequency || 'One Time';
        const isStrategyCall = isStrategyCallDeliverableName(matchingItem.name);
        const { billingPeriod, currentPeriod } = resolveMonthlyDeliverablePeriodContext(
          account,
          contractDataFromState,
          period
        );
        const currentPeriodLogs = filterLogsForCurrentPeriod(
          logs,
          period,
          currentPeriod,
          billingPeriod
        );
        const currentCompleted = currentPeriodLogs.reduce((sum, log) => sum + log.quantity, 0);
        const isCheckboxMode = featureData.quantity === '0' && isOneTimeStylePeriod(period);
        const programQty =
          featureData.quantity === '0' ? 1 : parseInt(featureData.quantity, 10) || 1;
        const purchasedAddOn = isStrategyCall
          ? resolveAddOnPurchased(deliverableProgress, billingPeriod ? billingPeriod.periodKey : currentPeriod)
          : 0;
        const effectiveProgramQuantity = programQty + purchasedAddOn;
        itemsWithDeliverables.push({
          ...matchingItem,
          programQuantity: effectiveProgramQuantity,
          isShowcase: featureData.showcase || false,
          completed: currentCompleted,
          lastCompleted: deliverableProgress.lastCompleted || null,
          currentPeriod,
          isCheckboxMode,
        });
      }
    });
  }

  const removedIds = new Set(account.removedDeliverableIds || []);
  itemsWithDeliverables = itemsWithDeliverables.filter((item) => !removedIds.has(item.id));
  const qtyOverrides = account.deliverableQuantityOverrides || {};
  if (Object.keys(qtyOverrides).length > 0) {
    itemsWithDeliverables = itemsWithDeliverables.map((item) => {
      const override = qtyOverrides[item.id];
      if (override === undefined) return item;
      return { ...item, programQuantity: override };
    });
  }
  const extraDeliverables = account.extraDeliverables || [];
  extraDeliverables.forEach((extra) => {
    const alaCarte = itemsData.find((i) => i.id === extra.id);
    if (!alaCarte || !alaCarte.hasDeliverable) return;
    const qty =
      typeof extra.quantity === 'number' ? extra.quantity : parseInt(extra.quantity, 10) || 1;
    const deliverableProgress = account.deliverables?.[alaCarte.id] || {};
    const logs = deliverableProgress.logs || [];
    const period = alaCarte.deliverable?.period || alaCarte.deliverable?.frequency || 'One Time';
    const { billingPeriod, currentPeriod } = resolveMonthlyDeliverablePeriodContext(
      account,
      contractDataFromState,
      period
    );
    const currentPeriodLogs = filterLogsForCurrentPeriod(logs, period, currentPeriod, billingPeriod);
    const currentCompleted = currentPeriodLogs.reduce((sum, log) => sum + log.quantity, 0);
    const mergedDeliverable = { ...(alaCarte.deliverable || {}), quantity: qty, period, frequency: period };
    itemsWithDeliverables.push({
      ...alaCarte,
      deliverable: mergedDeliverable,
      programQuantity: qty,
      completed: currentCompleted,
      lastCompleted: deliverableProgress.lastCompleted || null,
      currentPeriod,
      isCheckboxMode: qty === 1 && isOneTimeStylePeriod(period),
    });
  });

  // One-period-only deliverables: each entry is pinned to a specific billing-period key. Only
  // entries matching the CURRENT period are applied, so they vanish automatically when the period
  // rolls over. An entry whose item already appears BUMPS that row's quantity for this period; an
  // entry for a not-yet-listed item APPENDS a one-off row. Both are tagged oneTimePeriodKey so the
  // UI can badge them "This period only" and offer removal, and neither is ever carried forward.
  const periodDeliverables = Array.isArray(account.periodDeliverables)
    ? account.periodDeliverables
    : [];
  if (periodDeliverables.length > 0) {
    const currentPeriodKey = getCurrentDeliverablePeriodKey(account, contractDataFromState);
    periodDeliverables.forEach((entry) => {
      if (!entry || entry.periodKey !== currentPeriodKey) return;
      const addQty =
        typeof entry.quantity === 'number' ? entry.quantity : parseInt(entry.quantity, 10) || 0;
      if (addQty <= 0) return;

      const existing = itemsWithDeliverables.find((i) => i.id === entry.id);
      if (existing) {
        existing.programQuantity = (Number(existing.programQuantity) || 0) + addQty;
        existing.oneTimePeriodQuantity = (existing.oneTimePeriodQuantity || 0) + addQty;
        existing.oneTimePeriodKey = currentPeriodKey;
        return;
      }

      const alaCarte = itemsData.find((i) => i.id === entry.id);
      if (!alaCarte || !alaCarte.hasDeliverable) return;
      const deliverableProgress = account.deliverables?.[entry.id] || {};
      const logs = deliverableProgress.logs || [];
      const period = alaCarte.deliverable?.period || alaCarte.deliverable?.frequency || 'One Time';
      const { billingPeriod, currentPeriod } = resolveMonthlyDeliverablePeriodContext(
        account,
        contractDataFromState,
        period
      );
      const currentPeriodLogs = filterLogsForCurrentPeriod(logs, period, currentPeriod, billingPeriod);
      const currentCompleted = currentPeriodLogs.reduce(
        (sum, log) => sum + (Number(log.quantity) || 0),
        0
      );
      const mergedDeliverable = {
        ...(alaCarte.deliverable || {}),
        quantity: addQty,
        period,
        frequency: period,
      };
      itemsWithDeliverables.push({
        ...alaCarte,
        deliverable: mergedDeliverable,
        programQuantity: addQty,
        completed: currentCompleted,
        lastCompleted: deliverableProgress.lastCompleted || null,
        currentPeriod,
        isCheckboxMode: addQty === 1 && isOneTimeStylePeriod(period),
        oneTimePeriodKey: currentPeriodKey,
        oneTimePeriodQuantity: addQty,
        oneTimePeriodOnly: true,
      });
    });
  }

  return {
    itemsData,
    programItems: itemsWithDeliverables,
    programPriceData,
  };
}
